import { useState } from "react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical, Palette, Trash2 } from "lucide-react";
import type { ProsthesisStatus } from "@bookdental/schemas";
import { cn } from "@/lib/cn";
import { SeletorDeCorDoEstagio } from "./SeletorDeCorDoEstagio";

export type FluxoStage = {
  id: string;
  label: string;
  colorHex: string | null;
  completionBehavior?: ProsthesisStatus | null;
};

/** Rótulo do papel especial do estágio. Direção correta: marcar o serviço com o status move ele
 *  PRA CÁ — entrar no estágio nunca muda o status (ver `setProductionStage`). */
export const DESTINO_LABEL: Record<string, string> = {
  CONCLUIDO: "Recebe os concluídos",
  CANCELADO: "Recebe os cancelados",
};

type Props = {
  stage: FluxoStage;
  posicao: number;
  travado: boolean;
  /** Etapa de modelo não tem destino de status — a raia inteira é sem regra. */
  permiteDestino: boolean;
  /** Nome da trilha ("estágio do serviço"/"etapa do modelo") — entra nos rótulos de acessibilidade
   *  porque as duas raias podem ter etapas com o MESMO nome, e aí "Arrastar X" viraria ambíguo. */
  raiaLabel: string;
  onRenomear: (label: string) => void;
  onCor: (cor: string) => void;
  onDestino: (valor: ProsthesisStatus | null) => void;
  onExcluir: () => void;
};

/**
 * Uma etapa na raia vertical — arrastável pela alça, igual às colunas do quadro. É a mesma
 * mecânica do Kanban, só que aqui o que se organiza é o fluxo em si, não o trabalho dentro dele.
 */
export function FluxoStageCard({
  stage,
  posicao,
  travado,
  permiteDestino,
  raiaLabel,
  onRenomear,
  onCor,
  onDestino,
  onExcluir,
}: Props) {
  const cor = stage.colorHex ?? "#78716c";
  const [paleta, setPaleta] = useState(false);
  const [nome, setNome] = useState(stage.label);

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: stage.id,
    disabled: travado,
  });

  const salvarNome = () => {
    const limpo = nome.trim();
    if (limpo.length >= 2 && limpo !== stage.label) onRenomear(limpo);
    else setNome(stage.label);
  };

  return (
    <li
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={cn(
        "rounded-lg border bg-white p-2 dark:bg-card",
        isDragging ? "z-10 border-brand-400 opacity-90 shadow-lg" : "border-zinc-200 dark:border-border",
      )}
    >
      <div className="flex items-center gap-1.5">
        {!travado && (
          <button
            type="button"
            {...attributes}
            {...listeners}
            aria-label={`Arrastar ${stage.label} (${raiaLabel})`}
            className="flex h-7 w-5 shrink-0 cursor-grab items-center justify-center text-zinc-300 hover:text-zinc-500 active:cursor-grabbing dark:text-muted-foreground/50"
          >
            <GripVertical className="h-4 w-4" />
          </button>
        )}
        <span
          className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[10px] font-bold tabular-nums text-white"
          style={{ backgroundColor: cor }}
        >
          {posicao + 1}
        </span>

        {travado ? (
          <span className="min-w-0 flex-1 truncate text-[13px] font-semibold text-zinc-900 dark:text-foreground">
            {stage.label}
          </span>
        ) : (
          <input
            value={nome}
            onChange={(e) => setNome(e.target.value)}
            onBlur={salvarNome}
            onKeyDown={(e) => {
              if (e.key === "Enter") e.currentTarget.blur();
              if (e.key === "Escape") setNome(stage.label);
            }}
            maxLength={60}
            aria-label={`Nome de ${stage.label} (${raiaLabel})`}
            className="min-w-0 flex-1 rounded border border-transparent bg-transparent px-1 py-0.5 text-[13px] font-semibold text-zinc-900 hover:border-zinc-200 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:text-foreground dark:hover:border-border"
          />
        )}

        {!travado && (
          <div className="flex shrink-0 items-center">
            <button
              type="button"
              onClick={() => setPaleta((v) => !v)}
              aria-label={`Cor de ${stage.label} (${raiaLabel})`}
              aria-pressed={paleta}
              className="flex h-7 w-7 items-center justify-center rounded text-zinc-400 transition-colors hover:bg-zinc-100 dark:hover:bg-muted"
            >
              <Palette className="h-3.5 w-3.5" />
            </button>
            <button
              type="button"
              onClick={onExcluir}
              aria-label={`Excluir ${stage.label} (${raiaLabel})`}
              className="flex h-7 w-7 items-center justify-center rounded text-zinc-400 transition-colors hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-950/30 dark:hover:text-red-400"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
        )}
      </div>

      {permiteDestino && stage.completionBehavior && travado && (
        <span
          className="mt-1 inline-block rounded-full px-2 py-0.5 text-[10px] font-semibold"
          style={{ backgroundColor: `${cor}1a`, color: cor }}
        >
          {DESTINO_LABEL[stage.completionBehavior]}
        </span>
      )}

      {!travado && (
        <div className="mt-1.5 flex flex-wrap items-center gap-1.5 pl-6">
          {permiteDestino && (
            <select
              value={stage.completionBehavior ?? ""}
              onChange={(e) => onDestino((e.target.value || null) as ProsthesisStatus | null)}
              aria-label={`Papel de ${stage.label} (${raiaLabel})`}
              className="h-7 max-w-full rounded-md border border-zinc-200 bg-white px-1 text-[11px] text-zinc-700 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-border dark:bg-card dark:text-foreground"
            >
              <option value="">Sem papel especial</option>
              <option value="CONCLUIDO">{DESTINO_LABEL.CONCLUIDO}</option>
              <option value="CANCELADO">{DESTINO_LABEL.CANCELADO}</option>
            </select>
          )}
          {paleta && (
            <SeletorDeCorDoEstagio
              valor={stage.colorHex}
              onChange={(hex) => {
                onCor(hex);
                setPaleta(false);
              }}
              previa={stage.label}
            />
          )}
        </div>
      )}
    </li>
  );
}

/** Ponto de inserção entre duas etapas — é o que permite criar no meio do fluxo, não só no fim.
 *  Discreto até o mouse chegar perto, pra não poluir a leitura da sequência. */
export function PontoDeInsercao({ onInserir, rotulo }: { onInserir: () => void; rotulo: string }) {
  return (
    <li className="group relative flex h-3 items-center justify-center">
      <span className="absolute inset-x-0 top-1/2 h-px bg-transparent transition-colors group-hover:bg-brand-200 dark:group-hover:bg-primary/30" />
      <button
        type="button"
        onClick={onInserir}
        aria-label={rotulo}
        title={rotulo}
        className="relative z-10 flex h-5 w-5 items-center justify-center rounded-full border border-dashed border-zinc-300 bg-white text-[13px] font-bold leading-none text-zinc-400 opacity-0 transition-opacity hover:border-brand-400 hover:text-brand-600 focus-visible:opacity-100 group-hover:opacity-100 dark:border-border dark:bg-card"
      >
        +
      </button>
    </li>
  );
}
